import type { AgentProp } from "@circle-react/components/SettingsApp/CommunityBot/agentsQueries";
import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { createQueryKeyFactory } from "@circle-react/lib/queries/createQueryKeyFactory";
import { queryClient } from "@circle-react/providers/QueryClient/reactQueryConfig";
import type { Profile } from "@circle-react/types/CommunityBot/Profile";
import { infiniteQueryOptions, queryOptions } from "react-query";

export const aiAgentKey = createQueryKeyFactory("ai-agent");

interface ListParams {
  page: number;
  perPage: number;
}

interface AiAgentListPage {
  count: number;
  page: number;
  per_page: number;
  records: AgentProp[];
}

interface SearchParams {
  allowDirectMessages?: boolean | null;
  enabledStatus?: boolean | null;
  name?: string;
}

interface AiAgentSearchResponse {
  records: AgentProp[];
}

interface AiAgentSpotlightSearchResponse {
  records: Profile[];
}

interface MemberAvatarsResponse {
  records: Profile[];
}

export interface AiAgentCommentRecord {
  body_trix_content: string | null;
  created_at: string;
  editor: "trix" | "tiptap";
  id: number;
  post_name?: string;
  post_slug: string;
  post_truncated_content?: string;
  space_slug: string;
  tiptap_body: object | null;
  updated_at: string;
}

export interface AiAgentCommentsPage {
  count: number;
  has_next_page: boolean;
  page: number;
  per_page: number;
  records: AiAgentCommentRecord[];
}

const booleanKey = (value: boolean | null | undefined) => {
  if (typeof value !== "boolean") return "any";

  return value ? "true" : "false";
};

const searchApiParams = ({
  allowDirectMessages,
  enabledStatus,
  name = "",
}: SearchParams) => ({
  name,
  ...(typeof enabledStatus === "boolean" && { enabled: enabledStatus }),
  ...(typeof allowDirectMessages === "boolean" && {
    allow_direct_messages: allowDirectMessages,
  }),
});

export const aiAgentQueries = {
  list: ({ page, perPage }: ListParams) =>
    queryOptions<AiAgentListPage>({
      queryKey: aiAgentKey.key("list", page, perPage),
      queryFn: () =>
        reactQueryGet<AiAgentListPage>(
          internalApi.communityBot.agents.index({
            params: {
              page,
              per_page: perPage,
            },
          }),
        ),
    }),
  detail: (id: number) =>
    queryOptions<AgentProp>({
      queryKey: aiAgentKey.key("detail", id),
      queryFn: () =>
        reactQueryGet<AgentProp>(internalApi.communityBot.agents.show({ id })),
    }),
  search: ({
    allowDirectMessages = null,
    enabledStatus = null,
    name = "",
  }: SearchParams = {}) =>
    queryOptions<AiAgentSearchResponse>({
      queryKey: aiAgentKey.key(
        "search",
        name,
        booleanKey(enabledStatus),
        booleanKey(allowDirectMessages),
      ),
      queryFn: () =>
        reactQueryGet<AiAgentSearchResponse>(
          internalApi.communityBot.agents.search({
            params: searchApiParams({
              allowDirectMessages,
              enabledStatus,
              name,
            }),
          }),
        ),
    }),
  spotlightSearch: () =>
    queryOptions<AiAgentSpotlightSearchResponse>({
      queryKey: aiAgentKey.key("spotlight-search"),
      queryFn: () =>
        reactQueryGet<AiAgentSpotlightSearchResponse>(
          internalApi.communityBot.agents.indexForSearch(),
        ),
    }),
  memberAvatars: () =>
    queryOptions({
      queryKey: aiAgentKey.key("member-avatars"),
      queryFn: () =>
        reactQueryGet<MemberAvatarsResponse>(
          internalApi.communityBot.agents.indexForConversations(),
        ),
      select: ({ records }) => records,
    }),
  directMessageAvatars: () =>
    queryOptions({
      queryKey: aiAgentKey.key("direct-message-avatars"),
      queryFn: () =>
        reactQueryGet<MemberAvatarsResponse>(
          internalApi.communityBot.agents.indexForDirectMessages(),
        ),
      select: ({ records }) => records,
    }),
  comments: ({ profileId, perPage }: { profileId: number; perPage: number }) =>
    infiniteQueryOptions({
      getNextPageParam: lastPage =>
        lastPage.has_next_page ? lastPage.page + 1 : undefined,
      initialPageParam: 1,
      queryKey: aiAgentKey.key("comments", profileId, perPage),
      queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
        reactQueryGet<AiAgentCommentsPage>(
          internalApi.communityBot.agents.comments({
            id: profileId,
            params: { page: pageParam, per_page: perPage },
          }),
        ),
    }),
};

export const aiAgentInvalidations = {
  all: () => queryClient.invalidateQueries({ queryKey: aiAgentKey.root() }),
  list: () =>
    queryClient.invalidateQueries({ queryKey: aiAgentKey.key("list") }),
  detail: (id: number) =>
    queryClient.invalidateQueries({ queryKey: aiAgentKey.key("detail", id) }),
  memberAvatars: () =>
    queryClient.invalidateQueries({
      queryKey: aiAgentKey.key("member-avatars"),
    }),
};

// Mirrors the server order: the avatar with the latest conversation comes first.
export const moveMemberAvatarToFront = (avatarId: number) =>
  queryClient.setQueryData<MemberAvatarsResponse>(
    aiAgentKey.key("member-avatars"),
    prev => {
      const avatar = prev?.records.find(({ id }) => id === avatarId);
      if (!prev || !avatar) return prev;

      return {
        ...prev,
        records: [avatar, ...prev.records.filter(({ id }) => id !== avatarId)],
      };
    },
  );
